import React from 'react';
import Icon from '../../../components/AppIcon';

const SignaturesSection = () => {
  const signatures = [
    { 
      party: 'Client Organization', 
      signatory: 'John Smith', 
      title: 'Chief Executive Officer',
      date: 'December 28, 2024',
      time: '2:45 PM EST',
      status: 'signed',
      email: 'john.smith@client.com',
      ipAddress: '192.168.1.100',
      location: 'New York, NY, USA'
    },
    { 
      party: 'Service Provider', 
      signatory: 'Sarah Johnson', 
      title: 'Vice President, Legal',
      date: 'December 29, 2024',
      time: '10:30 AM EST',
      status: 'signed',
      email: 'sarah.johnson@provider.com',
      ipAddress: '192.168.1.200',
      location: 'San Francisco, CA, USA'
    }
  ];

  const witnesses = [
    {
      name: 'Michael Rodriguez',
      title: 'Legal Counsel',
      organization: 'Client Organization',
      date: 'December 28, 2024',
      email: 'michael.rodriguez@client.com'
    },
    {
      name: 'Emily Chen',
      title: 'Contract Manager',
      organization: 'Service Provider',
      date: 'December 29, 2024',
      email: 'emily.chen@provider.com'
    }
  ];

  const auditTrail = [
    {
      action: 'Document Created',
      user: 'System Administrator',
      timestamp: 'Dec 20, 2024 - 9:00 AM EST',
      details: 'Initial contract document generated'
    },
    {
      action: 'Sent for Review',
      user: 'Sarah Johnson',
      timestamp: 'Dec 22, 2024 - 2:15 PM EST',
      details: 'Document sent to legal teams for review'
    },
    {
      action: 'Review Completed',
      user: 'Legal Team',
      timestamp: 'Dec 26, 2024 - 4:30 PM EST',
      details: 'All parties completed document review'
    },
    {
      action: 'Signature Request Sent',
      user: 'System',
      timestamp: 'Dec 27, 2024 - 8:00 AM EST',
      details: 'Electronic signature requests dispatched'
    },
    {
      action: 'First Signature',
      user: 'John Smith',
      timestamp: 'Dec 28, 2024 - 2:45 PM EST',
      details: 'Client representative signed the agreement'
    },
    {
      action: 'Final Signature',
      user: 'Sarah Johnson',
      timestamp: 'Dec 29, 2024 - 10:30 AM EST',
      details: 'Service provider representative completed signing'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="PenTool" size={20} className="text-primary" />
          <h2 className="text-xl font-semibold text-foreground">Contract Signatures</h2>
        </div>
        
        <div className="space-y-6">
          {signatures?.map((signature, index) => (
            <div key={index} className="border border-border rounded-lg p-6 bg-muted/20">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {signature?.party}
                  </h3>
                  <p className="text-sm text-muted-foreground">Authorized Signatory</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <span className="text-sm font-medium text-success">Digitally Signed</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Signatory Name</label>
                  <p className="text-base text-foreground mt-1">{signature?.signatory}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Title</label>
                  <p className="text-base text-foreground mt-1">{signature?.title}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email Address</label>
                  <p className="text-base text-foreground mt-1 font-mono">{signature?.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Date & Time Signed</label>
                  <p className="text-base text-foreground mt-1 font-mono">
                    {signature?.date} at {signature?.time}
                  </p>
                </div>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Shield" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">Digital Signature Verification</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">IP Address:</span>
                    <span className="text-foreground font-mono ml-2">{signature?.ipAddress}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Location:</span>
                    <span className="text-foreground ml-2">{signature?.location}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Signature authenticated using DocuSign with timestamp verification and complete audit trail.
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Users" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Witnesses</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {witnesses?.map((witness, index) => (
            <div key={index} className="p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-base font-medium text-foreground">{witness?.name}</p>
                  <p className="text-sm text-muted-foreground">{witness?.title}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Organization:</span>
                  <span className="text-foreground ml-2">{witness?.organization}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <span className="text-foreground font-mono ml-2">{witness?.email}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Witnessed on:</span>
                  <span className="text-foreground font-mono ml-2">{witness?.date}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="FileSearch" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Audit Trail</h3>
        </div>
        
        <div className="space-y-4">
          {auditTrail?.map((entry, index) => (
            <div key={index} className="flex items-start space-x-4 p-4 bg-muted/20 rounded-lg">
              <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2"></div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-base font-medium text-foreground">{entry?.action}</p>
                  <span className="text-sm text-muted-foreground font-mono">{entry?.timestamp}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-1">by {entry?.user}</p>
                <p className="text-sm text-foreground">{entry?.details}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SignaturesSection;